import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import { useToast } from '../../../shared/components/Toast';
import { mensagemDeErro } from '../../pessoas/api';
import { PortalVacation } from '../../pessoas/api/alocacoes';
import { clientVacationTone, formatDate } from '../../pessoas/components/format';
import { PessoasModal } from '../../pessoas/components/PessoasModal';
import { Textarea } from '../../../shared/components/Textarea';
import { useDarCiencia, useFeriasDaEquipeDoCliente, useManifestarConflito } from '../../pessoas/hooks/alocacoes';

/**
 * Férias da equipe no portal do cliente (US-GP-015): só os profissionais
 * alocados com o gestor, sem dados pessoais, financeiros ou médicos.
 */
export default function PortalClienteFeriasPage() {
  const toast = useToast();
  const equipe = useFeriasDaEquipeDoCliente();
  const ciencia = useDarCiencia();
  const conflito = useManifestarConflito();
  const [alvo, setAlvo] = useState<{ vacation: PortalVacation; name: string } | null>(null);
  const [justificativa, setJustificativa] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function darCiencia(coverageId: string) {
    try {
      await ciencia.mutateAsync(coverageId);
      toast.success('Ciência registrada.');
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível registrar a ciência.'));
    }
  }

  async function enviarConflito() {
    if (!alvo?.vacation.coverageId) return;
    if (!justificativa.trim()) return setError('Explique o conflito.');
    try {
      await conflito.mutateAsync({ coverageId: alvo.vacation.coverageId, justification: justificativa.trim() });
      toast.success('Conflito enviado à empresa.');
      setAlvo(null);
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível enviar o conflito.'));
    }
  }

  if (equipe.isLoading) return <p className="text-sm text-gray-500">Carregando…</p>;
  if (equipe.isError) return <p className="text-sm text-red-600">Não foi possível carregar as férias da equipe.</p>;
  const lista = equipe.data ?? [];

  return (
    <div className="max-w-5xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Férias da equipe</h1>
        <p className="text-sm text-gray-600">Profissionais alocados sob sua responsabilidade.</p>
      </div>
      {lista.length === 0 ? (
        <p className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">Não há profissionais vinculados a você.</p>
      ) : (
        <ul className="space-y-3">
          {lista.map((p) => (
            <li key={p.allocationId} className="rounded-lg border border-gray-200 bg-white p-4 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-gray-900">{p.name}</p>
                  <p className="text-gray-600">
                    {[p.role, p.project, p.internalManager && `gestor: ${p.internalManager}`].filter(Boolean).join(' · ') || '—'}
                  </p>
                </div>
                {p.statusLabel && <span className={`rounded px-2 py-0.5 text-xs ${clientVacationTone(p.status)}`}>{p.statusLabel}</span>}
              </div>
              {p.hasAvailableVacation && (
                <p className="mt-1 text-xs text-gray-600">
                  Possui férias disponíveis ({p.periodsAvailable} período{p.periodsAvailable > 1 ? 's' : ''}).
                </p>
              )}
              {p.vacations.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {p.vacations.map((v, idx) => (
                    <li key={v.coverageId ?? idx} className="rounded border border-gray-100 bg-gray-50 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className={`rounded px-2 py-0.5 text-xs ${clientVacationTone(v.status)}`}>{v.statusLabel}</span>
                        {v.coverageId && ['aguardando_ciencia_cliente', 'ferias_aprovadas', 'ferias_proximas', 'em_ferias'].includes(v.status) && (
                          <span className="flex gap-2">
                            {!v.acknowledgedAt && (
                              <Button size="sm" onClick={() => darCiencia(v.coverageId!)} disabled={ciencia.isPending}>
                                <CheckCircle2 className="mr-1 h-4 w-4" /> Dar ciência
                              </Button>
                            )}
                            <Button size="sm" variant="secondary"
                              onClick={() => { setJustificativa(''); setError(null); setAlvo({ vacation: v, name: p.name }); }}>
                              Informar conflito
                            </Button>
                          </span>
                        )}
                      </div>
                      <ul className="mt-2 space-y-1 text-gray-700">
                        {v.installments.map((i) => (
                          <li key={i.startDate}>
                            {formatDate(i.startDate)} a {formatDate(i.endDate)} · {i.days} dias · retorno em {formatDate(i.returnDate)}
                            {p.suspendAccess && <span className="text-xs text-gray-500"> · {i.accessLabel}</span>}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-1 text-xs text-gray-600">
                        {v.needsSubstitute
                          ? v.substitute ? `Substituto: ${v.substitute.name}${v.substitute.role ? ` (${v.substitute.role})` : ''}` : 'Substituto a definir'
                          : 'Sem necessidade de substituto'}
                        {v.acknowledgedAt && ` · ciência em ${new Date(v.acknowledgedAt).toLocaleDateString('pt-BR')}`}
                      </p>
                      {v.conflicts.map((c, k) => (
                        <p key={k} className="text-xs text-amber-800">Conflito informado em {new Date(c.createdAt).toLocaleDateString('pt-BR')}: {c.justification}</p>
                      ))}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      <PessoasModal open={alvo !== null} title={`Conflito nas férias de ${alvo?.name ?? ''}`} submitLabel="Enviar"
        description="A empresa recebe a manifestação e avalia; as férias não são alteradas automaticamente."
        isSubmitting={conflito.isPending} submitError={error} onClose={() => setAlvo(null)} onSubmit={enviarConflito}>
        <Textarea label="Justificativa" value={justificativa} onChange={(e) => setJustificativa(e.target.value)} />
      </PessoasModal>
    </div>
  );
}
